import { Assets, EventEmitter } from "pixi.js";
import { sound } from "@pixi/sound";
import { JackpotHitData } from "../models/JackpotHitData";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type AudioPoolItem = { file: string; weight: number };
type AudioPool = AudioPoolItem[];

type AudioPhase = {
  /** SFX to play when the phase starts. */
  sfxPool?: AudioPool;
  /** Primary voiceover pool (announcement / attract). */
  voPool?: AudioPool;
  /** Secondary voiceover pool – HIT congrats ("Congratulations to the winner!"). */
  voPool2?: AudioPool;
  /** Closing SFX pool – played after the HIT celebration (applause). */
  sfxEndPool?: AudioPool;
  /**
   * Playback priority – higher number wins.
   *   4 = HIT  |  3 = Eligible  |  2 = Increment  |  1 = Reset
   */
  priority: number;
  /** Per-file VO cooldown (ms). Prevents the same line repeating too soon. */
  voCooldownMs?: number;
  /** SFX master volume (0–1). Defaults to DEFAULT_SFX_VOLUME. */
  sfxVolume?: number;
  /** VO master volume (0–1). Defaults to DEFAULT_VO_VOLUME. */
  voVolume?: number;
  /** Whether the SFX should loop until explicitly stopped. */
  sfxLoop?: boolean;
};

// ─────────────────────────────────────────────────────────────────────────────
// Tunable constants
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_SFX_VOLUME = 0.5;
const DEFAULT_VO_VOLUME = 1.0;
/** SFX volume is multiplied by this while a voiceover is speaking. */
const SFX_DUCK_FACTOR = 0.25;

// HIT sequence timings (§6 of the spec)
const HIT_VO1_DELAY_MIN_MS = 300; // random delay before announcement VO
const HIT_VO1_DELAY_MAX_MS = 800;
const HIT_VO2_MIN_MS = 2_000; // minimum elapsed ms since HIT before congrats VO

const TWO_HOURS_MS = 2 * 60 * 60 * 1_000;

// ─────────────────────────────────────────────────────────────────────────────

export class AudioManager extends EventEmitter {
  // ── Phase configuration ──────────────────────────────────────────────────

  private readonly audioPools: Record<string, AudioPhase>;

  // ── Asset cache ──────────────────────────────────────────────────────────

  /** Short-name → Sound object, populated from the PixiJS asset cache at init time. */
  private readonly sounds = new Map<string, any>();

  // ── Playback state ───────────────────────────────────────────────────────

  /** Priority of the currently active phase (0 = idle). */
  private activePriority = 0;

  /** Short name of the SFX currently playing / looping (for ducking). */
  private currentSfxFile: string | null = null;
  /** Base volume for the current SFX (restored after duck). */
  private currentSfxVolume = DEFAULT_SFX_VOLUME;
  /** The IMediaInstance returned by the last snd.play() – used for reliable stopping. */
  private currentSfxInstance: any | null = null;

  /** Whether a voiceover is currently playing. */
  private isVoPlaying = false;
  /** Reset was requested while HIT was playing; fire it once HIT finishes. */
  private resetPending = false;
  /** Stops the current VO and resolves its Promise (used for priority interruption). */
  private voCancel: (() => void) | null = null;
  /** Stops the current one-shot awaitable SFX and resolves its Promise. */
  private sfxCancel: (() => void) | null = null;

  // ── VO cooldown / repeat-avoidance ───────────────────────────────────────

  /** file name → timestamp after which the file may play again. */
  private readonly voCooldowns = new Map<string, number>();
  /** pool key → last-played file name (prevents immediate back-to-back repeats). */
  private readonly lastPlayedVo = new Map<string, string>();

  // ── Initialisation ───────────────────────────────────────────────────────

  private isInitialized = false;
  private readonly pendingRequests: Array<() => void> = [];
  /**
   * Incremented every time playHitSequence() starts.
   * Each invocation captures its own token; any check that sees a different
   * token knows it has been superseded and must abort immediately.
   */
  private hitSequenceToken = 0;

  // ─────────────────────────────────────────────────────────────────────────

  constructor() {
    super();
    this.audioPools = this.defineAudioPools();
  }

  // ── Phase definitions ─────────────────────────────────────────────────────

  private defineAudioPools(): Record<string, AudioPhase> {
    return {
      // ── Phase 1: Reset / Start ───────────────────────────────────────────
      // Voiceover plays first; coins SFX follows once the VO ends.
      reset: {
        sfxPool: [{ file: "jackpot_winner_coins_droped", weight: 1 }],
        voPool: [
          { file: "El_jackpot_vuelve_a_comenzar_Option_1", weight: 1 },
          { file: "El_jackpot_vuelve_a_comenzar_Option_2", weight: 1 },
          { file: "Nuevo_Jackpot_Nueva_emocion_Option_1", weight: 1 },
        ],
        priority: 1,
        voCooldownMs: 30_000,
        sfxVolume: DEFAULT_SFX_VOLUME,
        voVolume: DEFAULT_VO_VOLUME,
      },

      // ── Phase 2: Increment / Growing ─────────────────────────────────────
      // Looping SFX + random VO with a 2-hour per-file cooldown.
      increment: {
        sfxPool: [{ file: "Coins_Dropping_03", weight: 1 }],
        sfxLoop: true,
        voPool: [
          { file: "El_jackpot_esta_creciendo_Option_1", weight: 1 },
          { file: "El_jackpot_esta_creciendo_Option_2", weight: 1 },
          { file: "El_jackpot_sigue_aumentando_Option_3", weight: 1 },
          // ⚠ Not yet in manifest – add to assetpack when file is delivered
          { file: "El_premio_sube_la_emocion_tambien_Option_2", weight: 1 },
          { file: "Juega_y_gana_el_jackpot_Option_2", weight: 1 },
          { file: "Mira_como_crece_el_premio_Option_2", weight: 1 },
          { file: "El_jackpot_sigue_creciendo_Option_2", weight: 1 },
        ],
        priority: 2,
        voCooldownMs: TWO_HOURS_MS,
        sfxVolume: DEFAULT_SFX_VOLUME,
        voVolume: DEFAULT_VO_VOLUME,
      },

      // ── Phase 3: Eligible / Possible Hit ─────────────────────────────────
      // Higher suspense; interrupts increment. Same looping SFX, different VO pool.
      eligible: {
        sfxPool: [{ file: "Coins_Dropping_03", weight: 1 }],
        sfxLoop: true,
        voPool: [
          // ⚠ First two files not yet in manifest – add when delivered
          { file: "Siente_la_tension_del_Jackpot_Option_1", weight: 1 },
          { file: "Preparate_para_ganar_Option_1", weight: 1 },
          { file: "Mira_como_crece_el_premio_Option_1", weight: 1 },
          { file: "La_suerte_esta_de_tu_lado_Option_2", weight: 1 },
          { file: "La_emocion_continua_Option_2", weight: 1 },
          { file: "Juega_por_el_Jackpot_Option_2", weight: 1 },
          { file: "Hoy_es_tu_dia_Option_2", weight: 1 },
          { file: "Esto_se_pon_interesante_Option_1", weight: 1 },
          {
            file: "El_jackpot_puede_caer_en_cualquier_momento_Option_1",
            weight: 1,
          },
        ],
        priority: 3,
        voCooldownMs: TWO_HOURS_MS,
        sfxVolume: DEFAULT_SFX_VOLUME,
        voVolume: DEFAULT_VO_VOLUME,
      },

      // ── Phase 4: Jackpot HIT – highest priority ───────────────────────────
      // Full timed sequence: SFX → announcement VO → congrats VO → applause.
      hit: {
        sfxPool: [{ file: "Slot_Win_1", weight: 1 }],
        voPool: [
          // Announcement pool: "We have a winner!"
          { file: "Jackpot_Jackpot_Jaaaaaackpoooooot_Option_2", weight: 1 },
          { file: "Jackpot_Jackpot_Jaaaaackpooooot_Option_1", weight: 1 },
          { file: "Jaaaaackpoooot_Option_2", weight: 1 },
          { file: "Jackpot_ganado_Option_1", weight: 1 },
        ],
        voPool2: [
          // Congrats pool: "Congratulations to the winner!"
          { file: "Asi_se_gana_Option_2", weight: 1 },
          { file: "Un_aplauso_para_el_ganador_Option_2", weight: 1 },
          { file: "Felicidades_Option_1", weight: 1 },
          { file: "Disfruta_tu_premio_Option_1", weight: 1 },
        ],
        sfxEndPool: [{ file: "Applause_Sound_Effect", weight: 1 }],
        priority: 4,
        sfxVolume: DEFAULT_SFX_VOLUME,
        voVolume: DEFAULT_VO_VOLUME,
      },
    };
  }

  // ── Initialization ────────────────────────────────────────────────────────

  public async initialize(): Promise<void> {
    if (this.isInitialized) return;
    try {
      this.loadSoundsFromAssetpack();
      this.isInitialized = true;
      console.log(
        `AudioManager: initialized – ${this.sounds.size} sounds loaded`,
      );
      const pending = this.pendingRequests.splice(0);
      for (const fn of pending) fn();
    } catch (error) {
      console.error("AudioManager: initialization failed", error);
    }
  }

  private loadSoundsFromAssetpack(): void {
    // The "classic" bundle is loaded by Assets.loadBundle() in AssetLoader before
    // AudioManager.initialize() is called. The manifest registers each sound under
    // two aliases (trimExtensions: true in assetpack config):
    //   "classic/sounds/SoundName.wav"  and  "classic/sounds/SoundName"
    // We retrieve them via the shorter (no-extension) alias.
    const soundNames = [
      // Phase 1 – Reset
      "jackpot_winner_coins_droped",
      "El_jackpot_vuelve_a_comenzar_Option_1",
      "El_jackpot_vuelve_a_comenzar_Option_2",
      "Nuevo_Jackpot_Nueva_emocion_Option_1",
      // Phase 2 – Increment
      "Coins_Dropping_03",
      "El_jackpot_esta_creciendo_Option_1",
      "El_jackpot_esta_creciendo_Option_2",
      "El_jackpot_sigue_aumentando_Option_3",
      "El_premio_sube_la_emocion_tambien_Option_2", // ⚠ pending asset
      "Juega_y_gana_el_jackpot_Option_2",
      "Mira_como_crece_el_premio_Option_2",
      "El_jackpot_sigue_creciendo_Option_2",
      // Phase 3 – Eligible
      "Siente_la_tension_del_Jackpot_Option_1", // ⚠ pending asset
      "Preparate_para_ganar_Option_1", // ⚠ pending asset
      "Mira_como_crece_el_premio_Option_1",
      "La_suerte_esta_de_tu_lado_Option_2",
      "La_emocion_continua_Option_2",
      "Juega_por_el_Jackpot_Option_2",
      "Hoy_es_tu_dia_Option_2",
      "Esto_se_pon_interesante_Option_1",
      "El_jackpot_puede_caer_en_cualquier_momento_Option_1",
      // Phase 4 – HIT SFX
      "Slot_Win_1",
      "Applause_Sound_Effect",
      // Phase 4 – HIT announcement VO
      "Jackpot_Jackpot_Jaaaaaackpoooooot_Option_2",
      "Jackpot_Jackpot_Jaaaaackpooooot_Option_1",
      "Jaaaaackpoooot_Option_2",
      "Jackpot_ganado_Option_1",
      // Phase 4 – HIT congrats VO
      "Asi_se_gana_Option_2",
      "Un_aplauso_para_el_ganador_Option_2",
      "Felicidades_Option_1",
      "Disfruta_tu_premio_Option_1",
    ];

    for (const name of soundNames) {
      const snd = Assets.get(`classic/sounds/${name}`);
      if (snd) {
        this.sounds.set(name, snd);
      } else {
        console.warn(`AudioManager: classic/sounds/${name} not in asset cache`);
      }
    }

    console.log(
      `AudioManager: ${this.sounds.size}/${soundNames.length} sounds ready`,
    );
  }

  // ── Public phase triggers ─────────────────────────────────────────────────

  /** Phase 1 – Jackpot value reset to initial amount after a win or system start. */
  public onJackpotReset(): void {
    this.triggerPhase("reset");
  }

  /** Phase 2 – Jackpot value is actively increasing. */
  public onJackpotIncrement(): void {
    // this.triggerPhase("increment"); // TODO: re-enable when increment audio is ready
  }

  /** Phase 3 – Jackpot has entered eligible / possible-hit state. */
  public onJackpotEligible(): void {
    this.triggerPhase("eligible");
  }

  /** Phase 4 – Jackpot won. Executes the full timed hit celebration sequence. */
  public onJackpotHit(_hitData: JackpotHitData): void {
    this.playHitSequence();
  }

  /** Stops all audio immediately and resets the active priority to idle. */
  public stopAll(): void {
    // Invalidate any running hit sequence so it aborts at its next await checkpoint
    this.hitSequenceToken++;
    this.cancelCurrentVo();
    this.cancelCurrentSfx();
    // Belt-and-suspenders: directly stop every tracked sound.
    // sound.stopAll() only covers sounds in @pixi/sound's own registry;
    // iterating this.sounds covers everything loaded via PixiJS Assets.
    for (const snd of this.sounds.values()) {
      try {
        snd.stop();
      } catch (_) {}
    }
    sound.stopAll();
    this.activePriority = 0;
    // If a reset was queued while HIT was playing, fire it now
    if (this.resetPending) {
      this.resetPending = false;
      this.triggerPhase("reset");
    }
  }

  // ── Phase orchestration ───────────────────────────────────────────────────

  private triggerPhase(phaseName: string): void {
    if (!this.isInitialized) {
      this.pendingRequests.push(() => this.triggerPhase(phaseName));
      return;
    }

    const phase = this.audioPools[phaseName];
    if (!phase) {
      console.warn(`AudioManager: unknown phase '${phaseName}'`);
      return;
    }

    // Lower-priority events are normally dropped while a higher-priority phase is active.
    // Exception: reset is queued so it plays as soon as the current phase finishes.
    if (phase.priority < this.activePriority) {
      if (phaseName === "reset") this.resetPending = true;
      return;
    }

    // Higher-priority phase: interrupt current audio immediately
    if (phase.priority > this.activePriority) {
      this.cancelCurrentVo();
      this.cancelCurrentSfx();
    }

    this.activePriority = phase.priority;

    if (phaseName === "reset") {
      this.executeResetPhase(phase);
    } else {
      this.executeAttractPhase(phaseName, phase);
    }
  }

  /**
   * Phase 1 – Reset sequence:
   *   1. VO announces new jackpot cycle.
   *   2. Coins SFX plays once after the VO completes.
   */
  private async executeResetPhase(phase: AudioPhase): Promise<void> {
    const sfxVol = phase.sfxVolume ?? DEFAULT_SFX_VOLUME;
    const voVol = phase.voVolume ?? DEFAULT_VO_VOLUME;

    const voFile = this.pickFromPool(
      phase.voPool,
      "reset_vo",
      phase.voCooldownMs,
    );
    if (voFile) await this.playVoiceover(voFile, voVol);

    // If still the active phase, play the one-shot coins SFX and await its completion
    if (this.activePriority === phase.priority) {
      const sfxItem = this.selectFromPool(phase.sfxPool, "reset_sfx");
      if (sfxItem) await this.playOneShotSfxAwaitable(sfxItem.file, sfxVol);
    }

    // Release priority; this is a transient one-shot phase
    if (this.activePriority === phase.priority) this.activePriority = 0;
  }

  /**
   * Phases 2 & 3 – Attract sequence:
   *   • Starts (or keeps) the looping SFX.
   *   • Plays one VO from the pool if no VO is currently active.
   *     Per-file cooldowns (2 h) prevent the same line playing too often.
   */
  private async executeAttractPhase(
    phaseName: string,
    phase: AudioPhase,
  ): Promise<void> {
    const sfxVol = phase.sfxVolume ?? DEFAULT_SFX_VOLUME;
    const voVol = phase.voVolume ?? DEFAULT_VO_VOLUME;

    // Start looping SFX only if the same file isn't already playing
    const sfxItem = this.selectFromPool(phase.sfxPool, `${phaseName}_sfx`);
    if (sfxItem && this.currentSfxFile !== sfxItem.file) {
      this.playSfxLooping(sfxItem.file, sfxVol);
    }

    // Play one VO from the pool if none is currently active
    if (!this.isVoPlaying) {
      const voFile = this.pickFromPool(
        phase.voPool,
        `${phaseName}_vo`,
        phase.voCooldownMs,
      );
      if (voFile) await this.playVoiceover(voFile, voVol);
    }

    // Priority remains set while the looping SFX is playing.
    // It is cleared when cancelCurrentSfx() is called by a higher-priority phase or stopAll().
  }

  // ── HIT celebration sequence (§6 of the spec) ────────────────────────────

  private async playHitSequence(): Promise<void> {
    if (!this.isInitialized) {
      this.pendingRequests.push(() => this.playHitSequence());
      return;
    }

    // Claim a unique token for this invocation.
    // If a second hit fires while this one is awaiting, the token is incremented
    // and every subsequent check here returns false → this sequence aborts cleanly.
    const myToken = ++this.hitSequenceToken;
    const isCurrent = () => this.hitSequenceToken === myToken;

    const phase = this.audioPools.hit;
    const sfxVol = phase.sfxVolume ?? DEFAULT_SFX_VOLUME;
    const voVol = phase.voVolume ?? DEFAULT_VO_VOLUME;

    // Highest priority: stop all lower-priority audio immediately
    this.cancelCurrentVo();
    this.cancelCurrentSfx();
    sound.stopAll();
    this.activePriority = phase.priority;

    const hitStart = Date.now();

    // t = 0 ms ── Main HIT SFX starts immediately; plays once through
    const sfxItem = this.selectFromPool(phase.sfxPool, "hit_sfx");
    if (sfxItem) this.playSfxLooping(sfxItem.file, sfxVol, false);

    // t = +300..800 ms ── Announcement VO: "We have a winner! / Jackpot won!"
    await this.delay(
      HIT_VO1_DELAY_MIN_MS +
        Math.random() * (HIT_VO1_DELAY_MAX_MS - HIT_VO1_DELAY_MIN_MS),
    );

    if (isCurrent()) {
      const vo1 = this.pickFromPool(phase.voPool, "hit_vo1");
      if (vo1) await this.playVoiceover(vo1, voVol);
    }

    // t ≥ 2 s from HIT ── Congrats VO: "Congratulations! / Amazing victory!"
    if (isCurrent()) {
      const elapsed = Date.now() - hitStart;
      const remaining = Math.max(0, HIT_VO2_MIN_MS - elapsed);
      if (remaining > 0) await this.delay(remaining);
    }

    if (isCurrent()) {
      const vo2 = this.pickFromPool(phase.voPool2, "hit_vo2");
      if (vo2) await this.playVoiceover(vo2, voVol);
    }

    // End of celebration ── Play applause one-shot (cancelCurrentSfx is called
    // inside playOneShotSfxAwaitable, so the hit SFX finishes if it's still going)
    if (isCurrent()) {
      const endItem = this.selectFromPool(phase.sfxEndPool, "hit_sfx_end");
      if (endItem) await this.playOneShotSfxAwaitable(endItem.file, sfxVol);
    }

    // HIT sequence complete – release priority so phase 1/2/3 can resume
    if (isCurrent()) {
      this.activePriority = 0;
      if (this.resetPending) {
        this.resetPending = false;
        this.triggerPhase("reset");
      }
    }
  }

  // ── Playback primitives ───────────────────────────────────────────────────

  /**
   * Fire-and-forget SFX. Stops the previously playing SFX first.
   * The SFX is automatically ducked if a voiceover is currently active.
   */
  private playSfxLooping(file: string, volume: number, loop = true): void {
    const snd = this.sounds.get(file);
    if (!snd) {
      console.warn(`AudioManager: SFX '${file}' not found`);
      return;
    }

    this.cancelCurrentSfx();
    this.currentSfxFile = file;
    this.currentSfxVolume = volume;

    snd.volume = this.isVoPlaying ? volume * SFX_DUCK_FACTOR : volume;
    const result = snd.play({ loop });
    // play() returns IMediaInstance synchronously for pre-loaded sounds,
    // or a Promise<IMediaInstance> if the AudioContext isn't ready yet.
    if (result instanceof Promise) {
      result.then((instance) => {
        if (this.currentSfxFile === file) {
          // Still the active SFX – store the instance for later stopping
          this.currentSfxInstance = instance;
        } else {
          // cancelCurrentSfx() already ran before the instance was available;
          // stop the instance immediately so it doesn't play untracked.
          instance.stop();
        }
      });
    } else {
      this.currentSfxInstance = result;
    }
  }

  /**
   * One-shot SFX that returns a Promise resolving when playback ends naturally
   * or when `cancelCurrentSfx()` is called (e.g. by a higher-priority phase).
   */
  private playOneShotSfxAwaitable(file: string, volume: number): Promise<void> {
    return new Promise<void>((resolve) => {
      const snd = this.sounds.get(file);
      if (!snd) {
        console.warn(`AudioManager: SFX '${file}' not found`);
        resolve();
        return;
      }

      this.cancelCurrentSfx();
      this.currentSfxFile = file;
      this.currentSfxVolume = volume;

      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        if (this.currentSfxFile === file) this.currentSfxFile = null;
        this.currentSfxInstance = null;
        this.sfxCancel = null;
        resolve();
      };

      // Store cancel handle so cancelCurrentSfx() can resolve this Promise early
      this.sfxCancel = () => {
        if (this.currentSfxInstance) {
          this.currentSfxInstance.stop();
        } else {
          snd.stop();
        }
        settle();
      };

      snd.volume = this.isVoPlaying ? volume * SFX_DUCK_FACTOR : volume;
      const result = snd.play({ loop: false, complete: settle });
      if (result instanceof Promise) {
        result.then((instance) => {
          if (this.currentSfxFile === file) this.currentSfxInstance = instance;
        });
      } else {
        this.currentSfxInstance = result;
      }
    });
  }

  /**
   * Plays a voiceover. Ducks the active SFX for the duration.
   * Returns a Promise that resolves when the VO ends naturally or is cancelled.
   */
  private playVoiceover(file: string, volume: number): Promise<void> {
    return new Promise<void>((resolve) => {
      const snd = this.sounds.get(file);
      if (!snd) {
        console.warn(`AudioManager: VO '${file}' not found – skipping`);
        resolve();
        return;
      }

      this.isVoPlaying = true;
      this.duckCurrentSfx();

      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        this.isVoPlaying = false;
        this.voCancel = null;
        this.unduckCurrentSfx();
        resolve();
      };

      // Store cancel handle so cancelCurrentVo() can interrupt this VO
      this.voCancel = () => {
        snd.stop();
        settle();
      };

      snd.volume = volume;
      snd.play({ complete: settle });
    });
  }

  private cancelCurrentVo(): void {
    this.voCancel?.();
  }

  private cancelCurrentSfx(): void {
    if (!this.currentSfxFile) return;
    // Stop via the stored IMediaInstance (most reliable)
    if (this.currentSfxInstance) {
      this.currentSfxInstance.stop();
      this.currentSfxInstance = null;
    }
    // Also call Sound.stop() — this clears @pixi/sound's internal _instances
    // array and handles any case where the instance wasn't stored yet.
    const snd = this.sounds.get(this.currentSfxFile);
    if (snd) snd.stop();
    // sfxCancel may be set if an awaitable one-shot is pending; resolve it
    this.sfxCancel?.();
    this.sfxCancel = null;
    this.currentSfxFile = null;
    this.currentSfxVolume = DEFAULT_SFX_VOLUME;
  }

  private duckCurrentSfx(): void {
    if (!this.currentSfxFile) return;
    const snd = this.sounds.get(this.currentSfxFile);
    if (snd) snd.volume = this.currentSfxVolume * SFX_DUCK_FACTOR;
  }

  private unduckCurrentSfx(): void {
    if (!this.currentSfxFile) return;
    const snd = this.sounds.get(this.currentSfxFile);
    if (snd) snd.volume = this.currentSfxVolume;
  }

  // ── Pool selection ────────────────────────────────────────────────────────

  /**
   * Picks a random item from `pool` using weighted selection.
   *  • Respects per-file cooldowns.
   *  • Avoids playing the same VO twice in a row (when alternatives exist).
   *  • If every item is on cooldown, picks the one whose cooldown expires soonest
   *    so the pool never goes completely silent.
   */
  private selectFromPool(
    pool: AudioPool | undefined,
    poolKey: string,
  ): AudioPoolItem | null {
    if (!pool || pool.length === 0) return null;

    const now = Date.now();
    const lastFile = this.lastPlayedVo.get(poolKey);

    let candidates = pool.filter(
      (item) => (this.voCooldowns.get(item.file) ?? 0) <= now,
    );

    // All on cooldown – fall back to the item closest to expiry
    if (candidates.length === 0) {
      const sorted = [...pool].sort(
        (a, b) =>
          (this.voCooldowns.get(a.file) ?? 0) -
          (this.voCooldowns.get(b.file) ?? 0),
      );
      candidates = [sorted[0]];
    }

    // Avoid back-to-back repeat when alternatives exist
    if (candidates.length > 1 && lastFile) {
      const withoutLast = candidates.filter((item) => item.file !== lastFile);
      if (withoutLast.length > 0) candidates = withoutLast;
    }

    // Weighted random selection
    const total = candidates.reduce((s, i) => s + i.weight, 0);
    let rnd = Math.random() * total;
    for (const item of candidates) {
      rnd -= item.weight;
      if (rnd <= 0) return item;
    }
    return candidates[candidates.length - 1];
  }

  /**
   * Selects from `pool`, records it as last-played for this pool key,
   * and applies the per-file cooldown. Returns the file name or null.
   */
  private pickFromPool(
    pool: AudioPool | undefined,
    poolKey: string,
    cooldownMs?: number,
  ): string | null {
    const item = this.selectFromPool(pool, poolKey);
    if (!item) return null;
    this.lastPlayedVo.set(poolKey, item.file);
    if (cooldownMs) this.voCooldowns.set(item.file, Date.now() + cooldownMs);
    return item.file;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

export const audioManager = new AudioManager();
